import { useState, useEffect } from 'react';
import { Users, Plus, Edit, Trash2, X, Mail, User, Shield, Key, AlertCircle, CheckCircle, Eye, EyeOff, RotateCcw } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';

interface UserData {
  id: string;
  full_name: string;
  email: string;
  role: string;
  phone?: string;
  is_active: boolean;
  deleted_at: string | null;
  created_at: string;
}

const rolePermissions = {
  Superadmin: {
    description: 'Full system access including API integrations and database',
    access: ['All Features', 'API Integrations', 'Database Status', 'User Management', 'Billing'],
  },
  Admin: {
    description: 'Administrative access without API integrations',
    access: ['User Management', 'Company Settings', 'Security', 'Billing', 'All Operations'],
  },
  Dispatch: {
    description: 'Manage loads, dispatches, and driver assignments',
    access: ['Excel Board', 'Loads', 'Dispatches', 'Documents', 'Address Book'],
  },
  'Dispatch Manager': {
    description: 'Enhanced dispatch access with driver reassignment capabilities',
    access: ['Excel Board', 'Loads', 'Dispatches', 'Driver Reassignment', 'Documents', 'Address Book'],
  },
  Safety: {
    description: 'Safety compliance and inspections management',
    access: ['Excel Board (View Only)', 'Safety Group', 'Expenses'],
  },
  'Safety Manager': {
    description: 'Enhanced safety access with management capabilities',
    access: ['Excel Board', 'Safety Group', 'Expenses', 'Maintenance', 'Analytics'],
  },
  Maintenance: {
    description: 'Vehicle and equipment maintenance tracking',
    access: ['Trucks', 'Trailers', 'Maintenance', 'Inspections'],
  },
  Accounting: {
    description: 'Financial operations and billing management',
    access: ['Loads', 'Dispatches', 'Accounting Dashboard', 'Billing', 'Documents'],
  },
  'Accounting Manager': {
    description: 'Enhanced accounting access with management capabilities',
    access: ['Accounting Dashboard', 'Billing', 'Documents', 'Analytics', 'All Financial Operations'],
  },
  Afterhours: {
    description: 'After-hours operations access',
    access: ['Dashboard', 'Loads & Dispatching', 'Documents (Read-Only)'],
  },
  ELD: {
    description: 'Electronic Logging Device management',
    access: ['Dashboard', 'Excel Board (View Only)', 'ELD'],
  },
  Recruiting: {
    description: 'Recruiting and driver onboarding management',
    access: ['Recruiting CRM', 'All Drivers', 'Dolasci\\Odlasci', 'Airplane Tickets'],
  },
};

export function UserManagementView() {
  const { showToast } = useToast();
  const { profile } = useAuth();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'removed'>('active');
  const [searchTerm, setSearchTerm] = useState('');

  // Check if user has permission to manage users
  const canManageUsers = profile?.role === 'Superadmin' || profile?.role === 'superadmin' || profile?.role === 'Admin' || profile?.role === 'admin';

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      // Force fresh data from database, no cache
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      console.log('Loaded users from database:', data?.map(u => ({ name: u.full_name, email: u.email })));
      setUsers(data || []);
    } catch (error: any) {
      showToast('error', 'Failed to load users: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user: UserData) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleDelete = (user: UserData) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleResetPassword = (user: UserData) => {
    setSelectedUser(user);
    setShowResetPasswordModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedUser) return;

    try {
      // Soft-delete: keep the profile row so every historical reference
      // (dispatches, loads, statements, activity logs, accounting) still
      // resolves to the person's name. is_active=false also stops login.
      const { error } = await supabase
        .from('profiles')
        .update({ deleted_at: new Date().toISOString(), is_active: false })
        .eq('id', selectedUser.id);

      if (error) throw error;

      showToast('success', 'User removed. Data preserved for historical records.');
      setShowDeleteModal(false);
      setSelectedUser(null);
      loadUsers();
    } catch (error: any) {
      showToast('error', 'Failed to remove user: ' + error.message);
    }
  };

  const handleRestore = async (user: UserData) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ deleted_at: null, is_active: true })
        .eq('id', user.id);
      if (error) throw error;
      showToast('success', `${user.full_name} restored.`);
      loadUsers();
    } catch (error: any) {
      showToast('error', 'Failed to restore user: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="bg-purple-100 p-3 rounded-lg">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900">User Management</h2>
              <p className="text-slate-600">Manage user accounts and permissions</p>
            </div>
          </div>
          {canManageUsers && (
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
            >
              <Plus className="w-5 h-5" />
              Add User
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-4">
          <input
            type="text"
            placeholder="Search by name, email, or role..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden self-start md:self-auto">
            {(['active', 'inactive', 'removed', 'all'] as const).map(s => {
              const label = s === 'all' ? 'All' : s === 'active' ? 'Active' : s === 'inactive' ? 'Inactive' : 'Removed';
              const count = users.filter(u => {
                if (s === 'active') return u.is_active && !u.deleted_at;
                if (s === 'inactive') return !u.is_active && !u.deleted_at;
                if (s === 'removed') return !!u.deleted_at;
                return true;
              }).length;
              return (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-2 text-sm font-medium border-l first:border-l-0 border-slate-300 ${
                    statusFilter === s
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {label} <span className="opacity-75">({count})</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Users Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">User</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Email</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Role</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Created</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {(() => {
                const q = searchTerm.trim().toLowerCase();
                const visible = users.filter(u => {
                  // Bucket by tab: Active/Inactive show only non-removed rows; Removed is soft-deleted.
                  if (statusFilter === 'active' && (!u.is_active || u.deleted_at)) return false;
                  if (statusFilter === 'inactive' && (u.is_active || u.deleted_at)) return false;
                  if (statusFilter === 'removed' && !u.deleted_at) return false;
                  if (!q) return true;
                  return (
                    (u.full_name || '').toLowerCase().includes(q) ||
                    (u.email || '').toLowerCase().includes(q) ||
                    (u.role || '').toLowerCase().includes(q)
                  );
                });
                if (visible.length === 0) {
                  return (
                    <tr>
                      <td colSpan={6} className="px-6 py-8 text-center text-sm text-slate-500">
                        No users match the current filters.
                      </td>
                    </tr>
                  );
                }
                return visible.map((user) => (
                <tr key={user.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold">
                        {user.full_name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">{user.full_name}</div>
                        {user.phone && <div className="text-xs text-slate-500">{user.phone}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-slate-600">{user.email}</td>
                  <td className="px-6 py-4">
                    <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {user.deleted_at ? (
                      <span
                        className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-slate-200 text-slate-700"
                        title={`Removed ${new Date(user.deleted_at).toLocaleDateString()}`}
                      >
                        Removed
                      </span>
                    ) : (
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                        user.is_active
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-slate-600">{new Date(user.created_at).toLocaleDateString()}</td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {canManageUsers && user.deleted_at && (
                        <button
                          onClick={() => handleRestore(user)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-sm font-medium"
                          title="Restore user (re-enables login)"
                        >
                          <RotateCcw className="w-4 h-4" />
                          Restore
                        </button>
                      )}
                      {canManageUsers && !user.deleted_at && (
                        <>
                          <button
                            onClick={() => handleEdit(user)}
                            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                            title="Edit User"
                          >
                            <Edit className="w-4 h-4 text-slate-600" />
                          </button>
                          <button
                            onClick={() => handleResetPassword(user)}
                            className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
                            title="Reset Password"
                          >
                            <Key className="w-4 h-4 text-blue-600" />
                          </button>
                          <button
                            onClick={() => handleDelete(user)}
                            className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                            title="Remove User (keeps historical records)"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </>
                      )}
                      {!canManageUsers && (
                        <span className="text-sm text-slate-400">View Only</span>
                      )}
                    </div>
                  </td>
                </tr>
                ));
              })()}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <AddUserModal 
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            loadUsers();
          }}
        />
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <EditUserModal 
          user={selectedUser}
          onClose={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedUser(null);
            loadUsers();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedUser && (
        <DeleteConfirmationModal 
          user={selectedUser}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedUser(null);
          }}
          onConfirm={confirmDelete}
        />
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && selectedUser && (
        <ResetPasswordModal 
          user={selectedUser}
          onClose={() => {
            setShowResetPasswordModal(false);
            setSelectedUser(null);
          }}
        />
      )}
    </div>
  );
}

function AddUserModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const { showToast } = useToast();
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    role: 'Dispatch',
    phone: '',
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Check if email already exists
      const { data: existingUser, error: checkError } = await supabase
        .from('profiles')
        .select('email')
        .eq('email', formData.email)
        .maybeSingle();

      if (checkError) {
        console.warn('Email check failed, proceeding anyway:', checkError);
      }

      if (existingUser) {
        throw new Error('A user with this email already exists');
      }

      // Call the database function to create user
      // This bypasses email confirmation issues
      const { data: rpcData, error: rpcError } = await supabase.rpc('create_user_account', {
        user_email: formData.email,
        user_password: formData.password,
        user_full_name: formData.full_name,
        user_role: formData.role,
        user_phone: formData.phone || null,
        user_is_active: formData.is_active,
      });

      if (rpcError) {
        console.error('RPC Error:', rpcError);
        throw new Error(rpcError.message || 'Failed to create user account');
      }

      console.log('RPC Response:', rpcData);

      if (!rpcData || !rpcData.success) {
        const errorMsg = rpcData?.message || 'User creation failed';
        console.error('User creation failed:', errorMsg);
        throw new Error(errorMsg);
      }

      showToast('success', 'User created successfully. They can now log in with their credentials.');
      onSuccess();
    } catch (error: any) {
      let errorMessage = error.message;
      if (error.message.includes('Database error') || error.message.includes('500')) {
        errorMessage = 'Unable to create user. This may be due to Supabase authentication settings. Please contact your administrator.';
      }
      showToast('error', 'Failed to create user: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const selectedRolePermissions = rolePermissions[formData.role as keyof typeof rolePermissions];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-slate-900">Add New User</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* User Information */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-4">User Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <User className="w-4 h-4 inline mr-1" />
                  Full Name *
                </label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="John Doe"
                  required
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Email Address *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="john@company.com"
                  required
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <Key className="w-4 h-4 inline mr-1" />
                  Password *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-4 py-2 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Min 6 characters"
                    required
                    minLength={6}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    disabled={loading}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="+1 (555) 123-4567"
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          {/* Role Selection */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-4">
              <Shield className="w-5 h-5 inline mr-1" />
              Role & Permissions
            </h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Select Role *
              </label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                {Object.keys(rolePermissions).map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </div>

            {/* Role Description & Permissions */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm font-semibold text-blue-900 mb-2">{formData.role}</p>
              <p className="text-sm text-blue-700 mb-3">{selectedRolePermissions.description}</p>
              <div>
                <p className="text-xs font-semibold text-blue-900 mb-2">This role will have access to:</p>
                <div className="flex flex-wrap gap-2">
                  {selectedRolePermissions.access.map((permission) => (
                    <span
                      key={permission}
                      className="inline-flex px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700"
                    >
                      {permission}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Account Status
            </label>
            <select
              value={formData.is_active ? 'active' : 'inactive'}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'active' })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={loading}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditUserModal({ user, onClose, onSuccess }: { user: UserData; onClose: () => void; onSuccess: () => void }) {
  const { showToast } = useToast();
  const { profile } = useAuth();
  const [formData, setFormData] = useState({
    email: user.email,
    full_name: user.full_name,
    role: user.role,
    phone: user.phone || '',
    is_active: user.is_active,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData: any = {
        full_name: formData.full_name,
        role: formData.role,
        phone: formData.phone || null,
        is_active: formData.is_active,
      };

      // Note: Email cannot be changed through profiles table
      // Email is managed by Supabase Auth and synced automatically

      console.log('Updating user with data:', updateData);
      
      const { error, data } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id)
        .select();

      console.log('Update response:', { error, data });
      
      if (error) {
        console.error('Full error details:', error);
        throw error;
      }

      console.log('User updated successfully, new email:', formData.email);
      showToast('success', 'User updated successfully');
      
      // Close modal and reload users
      onClose();
      onSuccess();
    } catch (error: any) {
      console.error('Error updating user:', error);
      showToast('error', 'Failed to update user: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const selectedRolePermissions = rolePermissions[formData.role as keyof typeof rolePermissions];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-slate-900">Edit User</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* User Information */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-4">User Information</h3>
            
            {/* Email (Read-only - managed by Supabase Auth) */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <Mail className="w-4 h-4 inline mr-1" />
                Email Address (Managed by Authentication)
              </label>
              <div className="p-4 bg-slate-50 rounded-lg text-slate-900 font-medium">{user.email}</div>
              <p className="text-xs text-slate-500 mt-1">Email cannot be changed here. Contact system administrator to change authentication email.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <User className="w-4 h-4 inline mr-1" />
                  Full Name *
                </label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="John Doe"
                  required
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="+1 (555) 123-4567"
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          {/* Role Selection */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-4">
              <Shield className="w-5 h-5 inline mr-1" />
              Role & Permissions
            </h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Select Role *
              </label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
                disabled={loading}
              >
                {Object.keys(rolePermissions).map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </div>

            {/* Role Description & Permissions */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm font-semibold text-blue-900 mb-2">{formData.role}</p>
              <p className="text-sm text-blue-700 mb-3">{selectedRolePermissions.description}</p>
              <div>
                <p className="text-xs font-semibold text-blue-900 mb-2">This role will have access to:</p>
                <div className="flex flex-wrap gap-2">
                  {selectedRolePermissions.access.map((permission) => (
                    <span
                      key={permission}
                      className="inline-flex px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700"
                    >
                      {permission}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Account Status
            </label>
            <select
              value={formData.is_active ? 'active' : 'inactive'}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'active' })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={loading}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteConfirmationModal({ user, onClose, onConfirm }: { user: UserData; onClose: () => void; onConfirm: () => void }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Remove User</h2>
              <p className="text-slate-600">They can be restored later from the Removed tab</p>
            </div>
          </div>

          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-6">
            <p className="text-sm text-amber-900">
              Remove <strong>{user.full_name}</strong> ({user.email})?
              Login will be blocked immediately, but their dispatches, loads, statements, and activity history
              will remain linked to their name for accounting and audit records.
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold"
            >
              Remove User
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ResetPasswordModal({ user, onClose }: { user: UserData; onClose: () => void }) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<'email' | 'manual'>('email');
  const [resetSent, setResetSent] = useState(false);
  const [passwordSet, setPasswordSet] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);

  const handleSendResetEmail = async () => {
    setLoading(true);
    try {
      const { requestPasswordReset } = await import('../../services/passwordService');
      const result = await requestPasswordReset(user.email);

      if (!result.success) throw new Error(result.error || 'Failed to send reset email');

      setResetSent(true);
      showToast('success', 'Password reset email sent successfully');
    } catch (error: any) {
      showToast('error', 'Failed to send reset email: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async () => {
    if (newPassword.length < 8) {
      showToast('error', 'Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast('error', 'Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const { adminSetPassword } = await import('../../services/passwordService');
      const result = await adminSetPassword(user.id, newPassword);

      if (!result.success) throw new Error(result.error || 'Failed to set password');

      setPasswordSet(true);
      showToast('success', 'Password updated successfully');
    } catch (error: any) {
      showToast('error', 'Failed to set password: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
              <Key className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Reset Password</h2>
              <p className="text-slate-600">{user.full_name} ({user.email})</p>
            </div>
          </div>

          {!resetSent && !passwordSet && (
            <>
              {/* Mode selector */}
              <div className="grid grid-cols-2 gap-2 mb-6 p-1 bg-slate-100 rounded-lg">
                <button
                  onClick={() => setMode('email')}
                  className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                    mode === 'email' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Mail className="w-4 h-4 inline mr-1" />
                  Email Reset Link
                </button>
                <button
                  onClick={() => setMode('manual')}
                  className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                    mode === 'manual' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Key className="w-4 h-4 inline mr-1" />
                  Set Password
                </button>
              </div>

              {mode === 'email' ? (
                <>
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-6">
                    <p className="text-sm text-blue-800">
                      A password reset link will be emailed to <strong>{user.email}</strong>.
                      The user clicks the link and chooses their own new password.
                      The link expires in 1 hour.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={onClose}
                      className="flex-1 px-6 py-3 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSendResetEmail}
                      disabled={loading}
                      className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Sending...' : 'Send Reset Link'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                    <p className="text-sm text-amber-800">
                      You are setting a new password for this user directly. Share it with them
                      securely — they can change it later from their profile.
                    </p>
                  </div>

                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="w-full px-4 py-2 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="At least 8 characters"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Confirm New Password
                      </label>
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Repeat new password"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={onClose}
                      className="flex-1 px-6 py-3 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSetPassword}
                      disabled={loading || !newPassword || !confirmPassword}
                      className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Updating...' : 'Set Password'}
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {resetSent && (
            <>
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <p className="text-sm font-semibold text-green-800">Reset Email Sent!</p>
                </div>
                <p className="text-sm text-green-700">
                  A password reset link has been sent to {user.email}. The user should check their inbox and follow the instructions.
                </p>
              </div>

              <button
                onClick={onClose}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
              >
                Close
              </button>
            </>
          )}

          {passwordSet && (
            <>
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <p className="text-sm font-semibold text-green-800">Password Updated!</p>
                </div>
                <p className="text-sm text-green-700">
                  The new password for {user.full_name} is active immediately. Share it with them securely.
                </p>
              </div>

              <button
                onClick={onClose}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
              >
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
